import React, { FC, useRef } from 'react';

import { Flex } from '@adsk/alloy-react-box';
import { ChevronDownIcon, ChevronUpIcon } from '@adsk/alloy-react-icon';
import Overlay from '@adsk/alloy-react-overlay';
import theme from '@adsk/alloy-react-theme';

import { PreviewType } from '../../hooks/usePreview';
import { useRemoteOverrides } from '../../providers/RemoteOverridesProvider';
import i18n from '../../utils/i18n';
import OverridePanel from '../OverridePanel/OverridePanel';

type OverrideOverlayProps = {
  preview?: PreviewType;
  onPreviewResetClick: () => void;
  onRemoteOverrideResetClick: (scope: string) => void;
};

const OverrideOverlay: FC<OverrideOverlayProps> = ({
  preview,
  onPreviewResetClick,
  onRemoteOverrideResetClick,
}) => {
  const containerRef = useRef<HTMLDivElement | null>(null);
  const overrides = useRemoteOverrides();

  return (
    <Flex
      flexDirection="column"
      alignItems="flex-end"
      position="fixed"
      top={0}
      right={0}
      zIndex={1}
      ref={containerRef}
    >
      <Overlay
        placement="bottom"
        container={containerRef}
        renderTarget={({ targetProps, handleToggle, show }) => (
          <Flex
            {...targetProps}
            onClick={handleToggle}
            height="24px"
            textAlign="center"
            backgroundColor="pink500"
            padding="0 8px"
            justifyContent="center"
            alignItems="center"
            style={{
              cursor: 'pointer',
            }}
            typography="bodyMediumBold"
            data-testid="RemoteOverridesOverlay"
          >
            {i18n.t('PREVIEW_DETAILS')}
            {show ? (
              <ChevronUpIcon style={{ marginLeft: 4 }} size={12} />
            ) : (
              <ChevronDownIcon style={{ marginLeft: 4 }} size={12} />
            )}
          </Flex>
        )}
        renderOverlay={({ overlayProps }) => (
          <Overlay.Container placement="bottom" {...overlayProps}>
            <Flex
              backgroundColor="white"
              style={{ boxShadow: theme.shadows.lowElevation }}
              flexDirection="column"
            >
              {preview && (
                <OverridePanel
                  title={preview.bucketPath}
                  onResetClick={onPreviewResetClick}
                  tooltipContent={preview.bucketId}
                  resetDataTestId="exit-preview"
                />
              )}
              {Object.keys(overrides).map((scope) => (
                <OverridePanel
                  key={scope}
                  onResetClick={() => onRemoteOverrideResetClick(scope)}
                  title={scope}
                  tooltipContent={overrides[scope]}
                  backgroundColor="green500"
                  resetDataTestId={`${scope}-reset`}
                />
              ))}
            </Flex>
          </Overlay.Container>
        )}
      />
    </Flex>
  );
};

export default OverrideOverlay;
